// Third-party
import React from 'react';
import classNames from 'classnames';
import Dotdotdot from 'react-dotdotdot';
import parse, { HTMLReactParserOptions, Element as DOMHandlerElement } from 'html-react-parser';
import DOMPurify from 'dompurify';

// Namespaces
import * as ProposalNS from '../../Proposal';
import { getTextarea } from '../../utils/proposal';

// Types
import LocalizedString from '@/types/localized-string';
import AccessibilityDescription from '../misc/AccessibilityDescription';
import Locale from '@/types/locale';
import ProposalTextAreaData from '@/types/proposal-text-area-data';
import Nullable from '@/types/nullable';

type Props = {
  texts: ProposalNS.ProposalText[];
  className?: string;
  locale?: Locale;
  accessibilityDescription?: LocalizedString;
  proposal?: ProposalNS.Proposal;
  setTextAreaData?: (key: string, textAreaData: ProposalTextAreaData) => void;
};

/* eslint-disable-next-line react/prefer-stateless-function */
class ProposalHTML extends React.Component<Props> {
  options: HTMLReactParserOptions = {
    replace: domNode => {
      const isElement = domNode instanceof DOMHandlerElement && domNode.type === 'tag' && domNode.attribs;
      if (isElement && domNode.attribs.class === 'analysis-section' && domNode.attribs['data-config'] && this.props.proposal != null) {
        let obj: { type: Nullable<string>; config: Nullable<unknown> } = { type: null, config: null };
        // I had the hardest time with escape sequences, I came up with this.
        // Use 2 single quotes ('') to escape a double quote ("")
        try {
          const sanitized = domNode.attribs['data-config'].replaceAll("''", '"');
          obj = JSON.parse(sanitized);
        } catch (err) {}

        switch (obj.type) {
          case 'ABOUT': {
            const onSetTextAreaData = this.props.setTextAreaData != null ? this.props.setTextAreaData : () => {};
            return getTextarea(this.props.proposal, onSetTextAreaData, obj.config);
          }
        }
      }
    },
  };

  render() {
    const { texts, className, accessibilityDescription, locale } = this.props;

    return (
      <>
        {accessibilityDescription && <AccessibilityDescription description={accessibilityDescription} locale={locale} />}

        <div className={classNames('proposal-html', className)}>
          {texts &&
            texts.map((text, index) => {
              const tag = text.tag || 'div';

              if (text.clamp) {
                return (
                  <Dotdotdot key={index} className={text.className} useNativeClamp tagName={tag} clamp={text.clamp} splitOnChars={[]}>
                    {text.text}
                  </Dotdotdot>
                );
              }

              if (text.text.includes('data-renderer="dynamicComponents"')) {
                const content = parse(DOMPurify.sanitize(text.text), this.options);
                return React.createElement<React.HTMLProps<HTMLElement>>(
                  tag,
                  {
                    className: text.className,
                    key: text._id,
                  },
                  content,
                );
              }
              return React.createElement<React.HTMLProps<HTMLElement>>(tag, {
                className: text.className,
                key: text._id,
                dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(text.text) },
              });
            })}
        </div>
      </>
    );
  }
}

export type ProposalHTMLProps = Props;

export default ProposalHTML;
